import { normalizeWalletKey } from '@/lib/launchpad/store'

export type ColonyActivityKind = 'buy' | 'sell' | 'contribute' | 'withdraw' | 'submit' | 'claim'

export interface ColonyActivity {
  id: string
  kind: ColonyActivityKind
  projectId?: string
  projectName: string
  ticker: string
  summary: string
  amountUsd?: number
  tokens?: number
  createdAt: string
}

const ACTIVITY_KEY = 'penguin-pad-colony-activity'
const BALANCE_KEY = 'penguin-pad-token-balances'
const ACTIVITY_BY_WALLET_KEY = 'penguin-pad-colony-activity-by-wallet'
const BALANCE_BY_WALLET_KEY = 'penguin-pad-token-balances-by-wallet'
export const COLONY_EVENT = 'penguin-pad-colony-change'
const MAX_ACTIVITY = 50

type ActivityByWallet = Record<string, ColonyActivity[]>
type BalancesByWallet = Record<string, Record<string, number>>

function canUseStorage() {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined'
}

function readJson<T>(key: string, fallback: T): T {
  if (!canUseStorage())
    return fallback
  try {
    const raw = window.localStorage.getItem(key)
    if (!raw)
      return fallback
    return JSON.parse(raw) as T
  }
  catch {
    return fallback
  }
}

function writeJson(key: string, value: unknown) {
  if (!canUseStorage())
    return
  try {
    window.localStorage.setItem(key, JSON.stringify(value))
  }
  catch {
    // Ignore quota / private-mode failures in this mock UI.
  }
}

function notify() {
  if (typeof window !== 'undefined')
    window.dispatchEvent(new Event(COLONY_EVENT))
}

function isActivityList(raw: unknown): raw is ColonyActivity[] {
  return Array.isArray(raw)
}

function migrateActivity(): ActivityByWallet {
  const next = readJson<ActivityByWallet>(ACTIVITY_BY_WALLET_KEY, {})
  if (next && typeof next === 'object' && !Array.isArray(next))
    return next
  return {}
}

function migrateBalances(): BalancesByWallet {
  const next = readJson<BalancesByWallet>(BALANCE_BY_WALLET_KEY, {})
  if (next && typeof next === 'object' && !Array.isArray(next))
    return next
  return {}
}

function claimLegacyActivity(store: ActivityByWallet, walletKey: string): ColonyActivity[] {
  const existing = store[walletKey]
  if (Array.isArray(existing))
    return existing
  const legacy = readJson<ColonyActivity[]>(ACTIVITY_KEY, [])
  if (isActivityList(legacy) && legacy.length > 0) {
    store[walletKey] = legacy
    writeJson(ACTIVITY_BY_WALLET_KEY, store)
    if (canUseStorage())
      window.localStorage.removeItem(ACTIVITY_KEY)
    return legacy
  }
  return []
}

function claimLegacyBalances(store: BalancesByWallet, walletKey: string): Record<string, number> {
  const existing = store[walletKey]
  if (existing && typeof existing === 'object' && !Array.isArray(existing))
    return existing
  const legacy = readJson<Record<string, number>>(BALANCE_KEY, {})
  if (legacy && typeof legacy === 'object' && !Array.isArray(legacy) && Object.keys(legacy).length > 0) {
    store[walletKey] = legacy
    writeJson(BALANCE_BY_WALLET_KEY, store)
    if (canUseStorage())
      window.localStorage.removeItem(BALANCE_KEY)
    return legacy
  }
  return {}
}

export function subscribeColonyActivity(listener: () => void): () => void {
  if (typeof window === 'undefined')
    return () => {}
  const onStorage = (event: StorageEvent) => {
    if (
      event.key === ACTIVITY_KEY
      || event.key === BALANCE_KEY
      || event.key === ACTIVITY_BY_WALLET_KEY
      || event.key === BALANCE_BY_WALLET_KEY
    ) {
      listener()
    }
  }
  window.addEventListener(COLONY_EVENT, listener)
  window.addEventListener('storage', onStorage)
  return () => {
    window.removeEventListener(COLONY_EVENT, listener)
    window.removeEventListener('storage', onStorage)
  }
}

export function getColonyActivity(address?: string): ColonyActivity[] {
  if (!address?.trim())
    return []
  const walletKey = normalizeWalletKey(address)
  const store = migrateActivity()
  const list = claimLegacyActivity(store, walletKey)
  return Array.isArray(list) ? list : []
}

export function addColonyActivity(
  entry: Omit<ColonyActivity, 'id' | 'createdAt'>,
  address?: string,
): ColonyActivity | undefined {
  if (!address?.trim())
    return undefined
  const next: ColonyActivity = {
    ...entry,
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    createdAt: new Date().toISOString(),
  }
  const walletKey = normalizeWalletKey(address)
  const store = migrateActivity()
  const current = claimLegacyActivity(store, walletKey)
  store[walletKey] = [next, ...current].slice(0, MAX_ACTIVITY)
  writeJson(ACTIVITY_BY_WALLET_KEY, store)
  notify()
  return next
}

export function getTokenBalance(projectId: string, address?: string): number {
  if (!address?.trim())
    return 0
  const balances = getAllTokenBalances(address)
  const value = balances?.[projectId]
  return typeof value === 'number' && Number.isFinite(value) ? value : 0
}

export function setTokenBalance(projectId: string, balance: number, address?: string) {
  if (!address?.trim())
    return
  const walletKey = normalizeWalletKey(address)
  const store = migrateBalances()
  const current = { ...claimLegacyBalances(store, walletKey) }
  current[projectId] = Math.max(0, balance)
  store[walletKey] = current
  writeJson(BALANCE_BY_WALLET_KEY, store)
  notify()
}

export function getAllTokenBalances(address?: string): Record<string, number> {
  if (!address?.trim())
    return {}
  const walletKey = normalizeWalletKey(address)
  const store = migrateBalances()
  const balances = claimLegacyBalances(store, walletKey)
  return balances && typeof balances === 'object' ? balances : {}
}
